import * as THREE from "three";
import { OrbitControls } from "three/examples/jsm/controls/OrbitControls.js";
import { GLTFLoader } from "/node_modules/three/examples/jsm/loaders/GLTFLoader.js";
import * as dat from "dat.gui";

// post
import { EffectComposer } from "/node_modules/three/examples/jsm/postprocessing/EffectComposer.js";
import { RenderPass } from "/node_modules/three/examples/jsm/postprocessing/RenderPass.js";
import { ShaderPass } from "/node_modules/three/examples/jsm/postprocessing/ShaderPass.js";
import { UnrealBloomPass } from "./shader/bloom/bloompass.js";
import { SMAAPass } from "three/addons/postprocessing/SMAAPass.js";
import { OutputPass } from "three/addons/postprocessing/OutputPass.js";

import fragmentShader from "./shader/fragment.glsl";
import vertexShader from "./shader/vertex.glsl";

import LoaderManager from "./managers/LoaderManager";

import gsap from "gsap";

const clamp = (num, min, max) => Math.min(Math.max(num, min), max);

export default class Sketch {
	constructor(options) {
		if (options.dom === null) return;
		this.container = options.dom;
		this.settings = {
			progress: 0,
			frequency: 0.15,
			pointSize: isRetina() ? 2 : 1,
			lookAt: {
				x: 0,
				y: 0,
				z: 0,
			},
			debug: options.debug,
		};
		console.log(isRetina() ? 2 : 1);
		this.init();
	}

	init = async () => {
		const assets = [
			{
				name: "image",
				texture: __webpack_public_path__ + "../public/img/webgl.png",
			},
		];

		await LoaderManager.load(assets);

		this.scene = new THREE.Scene();
		//this.scene.fog = new THREE.Fog( 0x180e3f, 1, 2 );

		this.vp = {
			width: window.innerWidth,
			height: window.innerHeight,
			dpr: Math.min(devicePixelRatio, 2 || 1),
		};
		this.width = this.container.offsetWidth;
		this.height = this.container.offsetHeight;
		this.renderer = new THREE.WebGLRenderer({
			antialias: true,
			alpha: true,
		});
		this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
		this.renderer.setSize(this.width, this.height);
		this.container.appendChild(this.renderer.domElement);

		// Camera
		this.camera = new THREE.PerspectiveCamera(
			70,
			this.width / this.height,
			0.1,
			10000
		);
		this.camera.position.set(-100, 0, 250);

		// Controls
		this.controls = new OrbitControls(this.camera, this.renderer.domElement);
		this.controls.enableDamping = true;

		this.time = 0;
		this.clock = new THREE.Clock();

		// raycaset
		this.raycaster = new THREE.Raycaster();
		this.pointer = new THREE.Vector2();
		this.point = new THREE.Vector3();

		this.isPlaying = true;
		this.setParticles();
		this.resize();
		this.setting();
		this.postProcessing();
		this.initCursor();
		this.raycasterEvent();
		this.render();
		this.setupResize();
	};

	raycasterEvent() {
		let mesh = new THREE.Mesh(
			new THREE.PlaneGeometry(300, 150, 10, 10),
			new THREE.MeshBasicMaterial({ color: 0xff0000, wireframe: true })
		);
		// this.scene.add(mesh);

		let mouseMesh = new THREE.Mesh(
			new THREE.IcosahedronGeometry(10, 1),
			new THREE.MeshBasicMaterial({ color: 0xff0000, wireframe: true })
		);
		//this.scene.add(mouseMesh);

		window.addEventListener("pointermove", (event) => {
			this.pointer.x = (event.clientX / window.innerWidth) * 2 - 1;
			this.pointer.y = -(event.clientY / window.innerHeight) * 2 + 1;

			this.raycaster.setFromCamera(this.pointer, this.camera);
			const intersects = this.raycaster.intersectObjects([mesh]);

			if (intersects[0]) {
				//console.log(intersects[0].point)
				//mouseMesh.position.copy(intersects[0].point)
				this.point.copy(intersects[0].point);
			}
		});
	}

	postProcessing() {
		this.postProcessed = true;

		// POST PROCESSING
		this.composer = new EffectComposer(this.renderer);
		this.composer.setSize(this.width, this.height);
		const renderScene = new RenderPass(this.scene, this.camera);
		this.composer.addPass(renderScene);

		/*
		 */
		// BLOOM
		const params = {
			bloomStrength: 5.0,
			bloomThreshold: 0.0,
			bloomRadius: 0.0,
		};
		this.bloomPass = new UnrealBloomPass(
			new THREE.Vector2(window.innerWidth, window.innerHeight),
			5,
			0.8,
			0.85
		);
		this.bloomPass.threshold = params.bloomThreshold;
		this.bloomPass.strength = params.bloomStrength;
		this.bloomPass.radius = params.bloomRadius;
		this.composer.addPass(this.bloomPass);

		// bloom gui
		if (this.settings.debug) {
			const bloomFolder = this.gui.addFolder("Bloom");
			bloomFolder
				.add(this.bloomPass, "strength", 0, 5.0, 0.01)
				.name("Strength");
			bloomFolder
				.add(this.bloomPass, "threshold", 0, 1.0, 0.01)
				.name("Threshold");
			bloomFolder.add(this.bloomPass, "radius", 0, 1.0, 0.01).name("Radius");
			bloomFolder.open();
		}

		const pixelRatio = this.renderer.getPixelRatio();
		const pass = new SMAAPass(
			window.innerWidth * pixelRatio,
			window.innerHeight * pixelRatio
		);
		this.composer.addPass(pass);
	}

	setupResize() {
		window.addEventListener("resize", this.resize.bind(this));
	}

	resize() {
		this.width = window.innerWidth;
		this.height = window.innerHeight;
		this.renderer.setSize(this.width, this.height);
		this.camera.aspect = this.width / this.height;
		this.camera.updateProjectionMatrix();
	}

	setParticles() {
		const geometry = new THREE.BufferGeometry();

		const multiplier = 18;
		const nbLines = 9 * multiplier;
		const nbColumns = 16 * multiplier;

		const vertices = [];
		const initPositions = [];

		for (let i = 0; i < nbColumns; i++) {
			for (let j = 0; j < nbLines; j++) {
				const point = [i, j, 0];
				const iniPoint = [
					i - nbColumns / 2,
					j - nbLines / 2,
					THREE.MathUtils.randFloat(10, 250),
				];
				vertices.push(...point);
				initPositions.push(...iniPoint);
			}
		}

		const vertices32 = new Float32Array(vertices);
		const initPositions32 = new Float32Array(initPositions);

		geometry.setAttribute("position", new THREE.BufferAttribute(vertices32, 3));
		geometry.setAttribute(
			"initPosition",
			new THREE.BufferAttribute(initPositions32, 3)
		);

		this.number = geometry.attributes.position.array.length;
		let randoms = new Float32Array(this.number / 3);
		for (let i = 0; i < this.number / 3; i++) {
			randoms.set([Math.random()], i);
		}
		geometry.setAttribute("randoms", new THREE.BufferAttribute(randoms, 1));

		geometry.center();
		const material = new THREE.PointsMaterial();
		this.shaderMaterial = new THREE.ShaderMaterial({
			fragmentShader,
			vertexShader,
			uniforms: {
				uPointSize: { value: isRetina() ? 8 : 3 },
				uNbLines: { value: nbLines },
				uNbColumns: { value: nbColumns },
				uTexture: { value: LoaderManager.assets["image"].texture },
				uProgress: { value: this.settings.progress },
				uFrequency: { value: this.settings.frequency },
				uMouse: { value: new THREE.Vector3() },
				uTime: { value: 0 },
			},
			transparent: true,
			depthTest: false,
			depthWrite: false,
		});

		const mesh = new THREE.Points(geometry, this.shaderMaterial);

		this.scene.add(mesh);
	}

	setting() {
		if (this.settings.debug) {
			this.gui = new dat.GUI();
			// particles
			const particlesFolder = this.gui.addFolder("Particles");
			particlesFolder
				.add(this.settings, "progress", 0, 1, 0.01)
				.onChange(() => {
					this.shaderMaterial.uniforms.uProgress.value = this.settings.progress;
				});
			particlesFolder
				.add(this.settings, "frequency", 0, 1, 0.01)
				.onChange(() => {
					this.shaderMaterial.uniforms.uFrequency.value =
						this.settings.frequency;
				});
			particlesFolder.add(this.settings, "pointSize", 0, 20, 1).onChange(() => {
				this.shaderMaterial.uniforms.uPointSize.value = this.settings.pointSize;
			});
			particlesFolder.open();

			var obj = {
				Enter: () => {
					this.animateIn();
				},
			};
			this.gui.add(obj, "Enter");
			particlesFolder.open();

			var obj = {
				Resetcam: () => {
					this.resetcam();
				},
			};
			this.gui.add(obj, "Resetcam");

			// camera
			const cameraFolder = this.gui.addFolder("Camera");
			cameraFolder.add(this.camera.position, "x", -500, 500, 1);
			cameraFolder.add(this.camera.position, "y", -500, 500, 1);
			cameraFolder.add(this.camera.position, "z", -500, 500, 1);
			cameraFolder.add(this.scene.position, "x", -500, 500, 1);
			cameraFolder.add(this.scene.position, "y", -500, 500, 1);
			cameraFolder.open();
		}
	}

	enter() {
		gsap.fromTo(
			this.settings,
			{
				progress: 0,
			},
			{
				progress: 0.5,
				duration: 1,
				ease: "Power2.easeOut",
			}
		);
		gsap.to(this.camera.position, {
			z: 100,
			x: -100,
			duration: 1,
			ease: "Power2.easeOut",
			onComplete: () => {
				this.isEntered = true;
				this.animateIn();
			},
		});
	}

	animateIn() {
		const animDuration = 2;
		gsap.fromTo(
			this.settings,
			{
				progress: 0.5,
			},
			{
				progress: 1,
				duration: animDuration,
				ease: "Power4.easeInOut",
			}
		);
		gsap.fromTo(
			this.shaderMaterial.uniforms.uPointSize,
			{
				value: isRetina() ? 8 : 3,
			},
			{
				value: isRetina() ? 5 : 2,
				duration: animDuration,
				ease: "Power4.easeInOut",
				onUpdate: () => {
					this.settings.pointSize =
						this.shaderMaterial.uniforms.uPointSize.value;
				},
			}
		);
		if (window.innerWidth > 992) {
			gsap.fromTo(
				this.bloomPass,
				{ strength: 5 },
				{
					strength: 2,
					duration: animDuration,
					ease: "Power4.easeInOut",
				}
			);
			gsap.to(this.camera.position, {
				x: 0,
				z: 130,
				duration: animDuration,
				ease: "Power4.easeInOut",
			});
		} else {
			gsap.fromTo(
				this.bloomPass,
				{ strength: 5 },
				{
					strength: 0.5,
					duration: animDuration,
					ease: "Power4.easeInOut",
				}
			);
			gsap.to(this.camera.position, {
				x: 0,
				z: 500,
				duration: animDuration,
				ease: "Power4.easeInOut",
			});
		}
	}

	resetcam() {
		gsap.to(this.camera.position, {
			z: 100,
			x: 0,
			duration: 1,
			ease: "Power4.easeOut",
		});
	}

	setCamera(cameraProgress) {
		if (window.innerWidth > 992) {
			gsap.to(this.camera.position, {
				x: -54 * cameraProgress,
				z: 130 - 52 * cameraProgress,
				duration: 1,
				ease: "Power4.easeOut",
			});
			gsap.to(this.scene.position, {
				x: 56 * cameraProgress,
				duration: 1,
				ease: "Power4.easeOut",
			});
		} else {
			gsap.to(this.camera.position, {
				x: -54 * cameraProgress,
				z: 300 - 52 * cameraProgress,
				duration: 1,
				ease: "Power4.easeOut",
			});
			gsap.to(this.scene.position, {
				x: 56 * cameraProgress,
				duration: 1,
				ease: "Power4.easeOut",
			});
		}
	}

	setProgress(animProgress) {
		//this.settings.progress = 1 - clamp( animProgress, 0, 0.9 );
		this.settings.progress = 1 - animProgress;
		this.shaderMaterial.uniforms.uPointSize.value =
			(isRetina() ? 5 : 1) + (isRetina() ? 15 : 6) * animProgress;
	}

	initCursor() {
		this.cursor = {
			x: 0,
			y: 0,
		};
		window.addEventListener("mousemove", (event) => {
			this.cursor.x = event.clientX / this.width - 0.5;
			this.cursor.y = event.clientY / this.height - 0.5;
		});
	}

	render() {
		const elapsedTime = this.clock.getElapsedTime();
		this.shaderMaterial.uniforms.uTime.value = elapsedTime;

		this.shaderMaterial.uniforms.uMouse.value = this.point;

		this.shaderMaterial.uniforms.uProgress.value = this.settings.progress;

		// Update camera
		//if ( this.isEntered ) this.camera.position.x = this.cursor.x * 100;
		this.scene.rotation.y = THREE.MathUtils.lerp(
			this.scene.rotation.y,
			(this.cursor.x * Math.PI) / 10,
			0.1
		);
		this.scene.rotation.x = THREE.MathUtils.lerp(
			this.scene.rotation.x,
			(this.cursor.y * Math.PI) / 10,
			0.1
		);

		// Update Orbital Controls
		this.controls.update();

		if (this.postProcessed) {
			this.composer.render();
		} else {
			this.renderer.render(this.scene, this.camera);
		}

		requestAnimationFrame(this.render.bind(this));
	}
}

function isRetina() {
	return (
		(window.matchMedia &&
			(window.matchMedia(
				"only screen and (min-resolution: 192dpi), only screen and (min-resolution: 2dppx), only screen and (min-resolution: 75.6dpcm)"
			).matches ||
				window.matchMedia(
					"only screen and (-webkit-min-device-pixel-ratio: 2), only screen and (-o-min-device-pixel-ratio: 2/1), only screen and (min--moz-device-pixel-ratio: 2), only screen and (min-device-pixel-ratio: 2)"
				).matches)) ||
		(window.devicePixelRatio && window.devicePixelRatio >= 2)
	);
}
